import * as React from "react";
import {
  applySnapshot,
  getMembers,
  getSnapshot,
  IModelType,
  SnapshotIn,
  types,
} from "mobx-state-tree";
import {
  assign,
  createActor,
  enqueueActions,
  fromCallback,
  fromPromise,
  log,
  setup,
} from "xstate";
import * as z from "zod";

import { env } from "~/env";
import { generateObjectId } from "~/lib/utils";
import { WebSocket } from "~/lib/ws";

type Action = {
  id: string;
  clientTime: number;
  ack?: boolean;
  serverTime?: number;
  type: string;
  payload: any;
};

type StorageAdapter = {
  getItem: (id: string) => string | undefined;
  setItem: (id: string, value: string) => void;
  removeItem: (id: string) => void;
};

function createSyncedStoreActor<S extends IModelType<any, any>>(params: {
  storeModel: S;
  id: string;
  storageAdapter: StorageAdapter;
  storeFetch: (id: string) => Promise<SnapshotIn<S>>;
  getSessionToken: () => Promise<string | undefined>;
}) {
  const { id, storageAdapter, storeFetch, storeModel } = params;
  const syncedStore = types.compose(
    storeModel,
    types
      .model({
        lastServerVersion: types.optional(types.number, 0),
      })
      .actions((self) => ({
        setLastServerTime: (time: number) => {
          self.lastServerVersion = time;
        },
      })),
  );

  const machine = setup({
    types: {} as {
      context: {
        id: string;
        storage: StorageAdapter;
        store?: S["Type"];
        serverQueue: Map<string, Action>;
        clientQueue: Map<string, Action>;
        online: boolean;
      };
      events:
        | { type: "STOP" }
        | {
            type: "CLIENT_ACTION";
            payload: Action;
          }
        | {
            type: "SERVER_ACTION";
            payload: Action;
          }
        | { type: "REBASE" }
        | { type: "RETRY" }
        | { type: "RESET_CLIENT_ACTIONS" }
        | { type: "SOCKET_OPEN" }
        | { type: "SOCKET_CLOSE" }
        | { type: "SYNC_ACTIONS" }
        | { type: "ALL_ACTIONS_SENT" }
        | { type: "CLIENT_ACTION_ACK"; payload: { id: string } };
    },
    actors: {
      fetchStoreFromApi: fromPromise(async function ({
        input,
      }: {
        input: {
          id: string;
        };
      }) {
        return await storeFetch(input.id);
      }),
      socket: fromCallback<
        { type: "STOP" } | { type: "SEND_ACTION"; payload: Action },
        { accountId: string }
      >(function ({ receive, sendBack, input: { accountId } }) {
        console.info("starting websocket");
        const ws = new WebSocket(
          `${env.EXPO_PUBLIC_SYNC_API_URL}/account/${accountId}`,
        );
        ws.onopen = async function () {
          const token = await params.getSessionToken();
          if (token) {
            ws.send(JSON.stringify({ type: "LOGIN", payload: { token } }));
            console.log("ws login sent");
          } else {
            throw new Error("no token");
          }
        };
        ws.onmessage = function (event) {
          console.log("ws message", event.data);
          try {
            const data = JSON.parse(event.data);
            const message = socketMessagesSchema.parse(data);
            if (message.type === "ERROR") {
              console.error(message.error);
            } else if (message.type === "READY") {
              sendBack({ type: "SOCKET_OPEN" });
            } else if (message.type === "ACK") {
              sendBack({
                type: "CLIENT_ACTION_ACK",
                payload: { id: message.id },
              });
            } else if (message.type === "SERVER_ACTION") {
              sendBack({ type: "SERVER_ACTION", payload: message.payload });
            }
          } catch (e) {
            if (e instanceof z.ZodError) {
              console.error("message validation error", e.errors);
            }
            console.error("error handling message here", e);
          }
        };
        ws.onerror = function (event) {
          console.error("ws error", event);
        };
        ws.onclose = function () {
          sendBack({ type: "SOCKET_CLOSE" });
          console.log("ws closed");
        };
        receive((event) => {
          if (event.type === "STOP") {
            ws.send(JSON.stringify({ type: "LOGOUT" }));
            ws.close();
          }
          if (event.type === "SEND_ACTION") {
            ws.send(
              JSON.stringify({ type: "CLIENT_ACTION", payload: event.payload }),
            );
          }
        });
      }),
    },
    actions: {
      // store
      loadStore: assign({
        store: ({ context }) => {
          try {
            const storedData = context.storage.getItem(`${context.id}:store`);
            if (storedData) {
              if (context.store) {
                applySnapshot(context.store, JSON.parse(storedData));
                return context.store;
              } else {
                return syncedStore.create(JSON.parse(storedData));
              }
            }
          } catch (e) {
            console.error(e);
          }
        },
      }),
      loadStoreFromServerSnapshot: assign({
        store: ({ context, event }) => {
          if ("output" in event) {
            if (context.store) {
              applySnapshot(context.store, event.output);
              return context.store;
            }
            return syncedStore.create(event.output as SnapshotIn<S>);
          }
        },
      }),
      persistStore: ({ context }) => {
        if (context.store) {
          const snapshot = getSnapshot(context.store);
          context.storage.setItem(
            `${context.id}:store`,
            JSON.stringify(snapshot),
          );
        }
      },
      // server queue
      loadServerQueue: ({ context }) => {
        try {
          const storedData = context.storage.getItem(
            `${context.id}:server-queue`,
          );
          if (storedData) {
            const data = JSON.parse(storedData);
            if (Array.isArray(data)) {
              data.forEach((action: Action) => {
                context.serverQueue.set(action.id, action);
              });
            }
          }
        } catch (e) {
          console.error(e);
        }
      },
      persistServerQueue: ({ context }) => {
        const snapshot = Array.from(context.serverQueue.values());
        context.storage.setItem(
          `${context.id}:server-queue`,
          JSON.stringify(snapshot),
        );
      },
      enqueueServerAction: ({ context, event }) => {
        if (event.type === "SERVER_ACTION") {
          context.serverQueue.set(event.payload.id, event.payload);
        }
      },
      applyServerActions: ({
        // this should be renamed as this should only run on init or rebase
        context: { serverQueue, clientQueue, store },
      }) => {
        if (store) {
          const actions = Array.from(serverQueue.values());
          actions.forEach((action) => {
            const { id, type, payload } = action;
            if (typeof store[type] === "function") {
              store[type](payload);
            }
            clientQueue.delete(id);
          });
          serverQueue.clear();
        }
      },
      applyServerAction: ({ context, event }) => {
        if (context.store && event.type === "SERVER_ACTION") {
          const { type, payload } = event.payload;
          if (typeof context.store[type] === "function") {
            context.store[type](payload);
          }
        }
      },
      // client queue
      loadClientQueue: ({ context }) => {
        try {
          const storedData = context.storage.getItem(
            `${context.id}:client-queue`,
          );
          if (storedData) {
            const data = JSON.parse(storedData);
            if (Array.isArray(data)) {
              data.forEach((action: Action) => {
                context.clientQueue.set(action.id, action);
              });
            }
          }
        } catch (e) {
          console.error(e);
        }
      },
      applyClientActions: ({ context: { clientQueue, store } }) => {
        if (store) {
          const actions = Array.from(clientQueue.values());
          actions.forEach((action) => {
            const { type, payload } = action;
            if (typeof store[type] === "function") {
              store[type](payload);
            }
          });
        }
      },
      enqueueClientAction: ({ context, event }) => {
        if (event.type === "CLIENT_ACTION") {
          context.clientQueue.set(event.payload.id, event.payload);
        }
      },
      persistClientQueue: ({ context }) => {
        const snapshot = Array.from(context.clientQueue.values());
        context.storage.setItem(
          `${context.id}:client-queue`,
          JSON.stringify(snapshot),
        );
      },
    },
    guards: {
      isOnline: ({ context }) => context.online,
      isOffline: ({ context }) => !context.online,
      isClientQueueEmpty: ({ context }) => context.clientQueue.size === 0,
      isClientQueueAcked: ({ context }) => {
        return Array.from(context.clientQueue.values()).every(
          (a) => a.ack === true,
        );
      },
      isServerQueueEmpty: ({ context }) => context.serverQueue.size === 0,
      isStoreEmpty: ({ context }) =>
        !context.store || context.store.lastServerTime === 0,
    },
  }).createMachine({
    /** @xstate-layout N4IgpgJg5mDOIC5QAoC2BDAxgCwJYDswBKAOgNwBcBiAbQAYBdRUABwHtZLc39mQAPRAFoA7ADYAnCQBMAVhEBmABwKFdERNnqANCACeiaXQCMJBRLHG6s6dIAsYsUusBfF7rRY8hUuWo1jJiQQdk4Kbl5gwQQhO2kRM0tjMXMRO2NpZwVdAwQ5WRJZVXE7CVUxERFjNw8MHAJiEgAzMAp6-CgAMQAnNlQAQRZcKggeMDJ8ADc2AGtxz3bGlrbvLt6BoYQCacx0cJ56BkO+UK4ePmjYkSVC6QkJO4VjY3FpHMQ7OgUSKzFpBXyIjoEhedhqIAW3iWrXaaz6g2GYG6vW6JBYABs9k02N1UCRIQ1SMtYT14Zttmxdvt8IdjsFTtSLsIlH8zPYbHZrrIxHQ7Ep3ghZBZCsZnJpZLIlCDruCCT4SOi2OgIAQoABRZE4qgAJTVABVtQBNOmsDhnSKgS5OOxmYw2JRVXlCtICsRFEgSdR2yXWdKWWV1KGkbpgZV6KgAYQAMgBJNUAOT1AH1+hG9TGAPLxk0hM2MqIfO4kJTGT5fYEpSrZfSIFQ25S2YG2WRWJSyANeQkkENhqgAZTV2oAaoOU2nM9nGCc8xEmQgNNIPekqvcVA2RK7hV8JU5JZlfh3FsHQxBwwPh6PU+mswEgqawrOCwglKVCg4Usoim7gQKJQUJEodx0I4RhxG2h5Bt2J7hrqABC-QDjmDKPpahjpCQfLcgBFSgbuAovlInzpJkfyghYEFdj2p46mqA7JtGcaJmO17xn2SEzucT4lnQJAVC2koAikDzVrkchmJKTjNqKijAhR8pUWeeoZgACuxD6cahgpAjIJYtkogFWHQP41ggGQ8byVTclomgsuocmNApZAQOiYD9hmEYANL6km0YZohU70hxFoCMIzxSAokgpBkzzssorrxD8wHxJk1y2I49nHmGTkuf2hrxhGzETmxAX3uac6eiQ4hWNZdBtkKfwCnI3wpZItjSMkZQZVBWVsE0TTog0bmed5KkJmpZVPkIdqmCIWiln8Tp2HYImIE8YgeuoLIKJysilDYXWOTgYCYDMACKACuYCXbQJW5upwWXFoxYKGk+nWK1EVvCZyg8SRzy1W2Ih3O27gQoGlHQSQR0nRdV2ube073XOVi8YCDySkCgkSAKDiLg8DwOJUihOAoB2Q9DZ2XddNDSHed0TZp9jfEULZ8gtPK7Ru33LYlzwZBF8hpWIZNZbAYD4CqHT9Jg1KRrGCbJleE5jh5435ppS0FC9TggiC7VqGUjU88CEilOkcQaFoYKg3KDmQ2LEuqtLsv9FGUaFVmfZJgOiZqyhIV5KKhR-JkdBGJUfqunaGEOMBRRpGkkgi6eJAO5LUDOxEQ1efRUZ+WqfsaQHvzFkYkq45zL6bt8RmVMY5j-DuShuKD+BsBAcB8LbRCIwzAdCCCPEAnYHJcjyfIClNEoyMkwE8rV6QvdIXV+L36v95IUhGTy9hfDy0mT+1iRFPpEivCof5dcSqykhsuBr-7lwlt8cTxNyvJtlX31pCQ6gVpIQMQRJxtuDeUiplSqg1CiB+RdLhVQwhYWaYcnQvjEAKNIv0P6L1qttBwydcilXXpcYw9xKpzVxotZak9ng-BeJIZas0HhODSPg7KYAYEPUQM8G0RRrDtQdJUT0Gh4rrT5JUF8-wMESFYb1fqDQOFzgHjcIUQonj6yBE6X8CU+Rh0+MPUUdkQGdnkuTbAx1KZwwUZNaqZgvQKF4S+QRdgBSlhuEDXkqgLByFKM3IxR5uopzTk7GW-tkKwI+G2DCvIjAWHMMCKoK1BQqF-mIEeUp7jqEyKTFuQA */
    initial: "init",
    context: () => {
      return {
        id,
        storage: storageAdapter,
        serverQueue: new Map(),
        clientQueue: new Map(),
        online: false,
      };
    },
    entry: enqueueActions(({ enqueue }) => {
      enqueue(log("synced store init"));
      enqueue("loadStore");
      enqueue("loadServerQueue");
      enqueue("loadClientQueue");
      enqueue("applyServerActions");
      enqueue("persistStore");
      enqueue("persistServerQueue");
      enqueue("applyClientActions");
      enqueue("persistClientQueue");
      enqueue(log("synced store init done"));
    }),
    states: {
      init: {
        always: [
          {
            guard: "isStoreEmpty",
            target: "fetchingFromApi",
          },
          {
            target: "ready",
          },
        ],
      },
      fetchingFromApi: {
        entry: log("fetching store from api"),
        invoke: {
          src: "fetchStoreFromApi",
          input: ({ context }) => ({
            id: context.id,
            store: context.store,
          }),
          onDone: {
            target: "ready",
            actions: enqueueActions(({ enqueue }) => {
              enqueue("loadStoreFromServerSnapshot");
              enqueue("persistStore");
            }),
          },
          onError: "loadingError",
        },
      },
      loadingError: {
        on: {
          RETRY: "fetchingFromApi",
        },
      },
      ready: {
        entry: log("synced store ready"),
        invoke: {
          id: "socket",
          src: "socket",
          input: ({ context }) => ({ accountId: context.id }),
        },
        initial: "checkQueue",
        states: {
          idle: {
            on: {
              SOCKET_CLOSE: {
                target: "offline",
              },
              SYNC_ACTIONS: {
                target: "checkQueue",
              },
            },
          },
          offline: {
            entry: assign({ online: false }),
            on: {
              SOCKET_OPEN: {
                target: "checkQueue",
                actions: assign({ online: true }),
              },
            },
          },
          checkQueue: {
            always: [
              {
                guard: "isOffline",
                target: "offline",
              },
              {
                guard: "isClientQueueAcked",
                target: "idle",
              },
              {
                target: "sendingAction",
              },
            ],
          },
          sendingAction: {
            entry: enqueueActions(({ enqueue, context }) => {
              const action = Array.from(context.clientQueue.values()).find(
                (a) => a.ack !== true,
              );
              if (action) {
                console.log("sending action", action);
                enqueue.sendTo("socket", {
                  type: "SEND_ACTION",
                  payload: action,
                });
              } else {
                console.log("all actions sent");
                enqueue.raise({ type: "ALL_ACTIONS_SENT" });
              }
            }),
            on: {
              CLIENT_ACTION_ACK: {
                actions: enqueueActions(
                  ({
                    enqueue,
                    context,
                    event: {
                      payload: { id },
                    },
                  }) => {
                    const action = context.clientQueue.get(id);
                    if (action) action.ack = true;
                    enqueue("persistClientQueue");
                  },
                ),
                target: "checkQueue",
              },
              ALL_ACTIONS_SENT: {
                target: "idle",
              },
              SOCKET_CLOSE: {
                target: "offline",
              },
            },
          },
        },
        on: {
          CLIENT_ACTION: {
            actions: enqueueActions(({ enqueue }) => {
              enqueue("enqueueClientAction");
              enqueue("persistClientQueue");
              enqueue.raise({ type: "SYNC_ACTIONS" });
            }),
          },
          SERVER_ACTION: [
            {
              guard: "isClientQueueEmpty",
              actions: enqueueActions(({ enqueue }) => {
                enqueue("applyServerAction");
                enqueue("persistStore");
              }),
            },
            {
              actions: enqueueActions(({ enqueue }) => {
                enqueue.cancel("REBASE");
                enqueue("enqueueServerAction");
                enqueue.raise({ type: "REBASE" }, { delay: 300 });
              }),
            },
          ],
          REBASE: {
            actions: enqueueActions(({ enqueue }) => {
              enqueue(log("rebasing"));
              enqueue("loadStore");
              enqueue("applyServerActions");
              enqueue("persistStore");
              enqueue("persistServerQueue");
              enqueue("applyClientActions");
              enqueue("persistClientQueue");
            }),
          },
          RESET_CLIENT_ACTIONS: {
            actions: enqueueActions(({ enqueue }) => {
              enqueue(assign({ clientQueue: () => new Map() }));
              enqueue("persistClientQueue");
              enqueue.raise({ type: "REBASE" });
            }),
          },
          STOP: {
            actions: enqueueActions(({ enqueue }) => {
              enqueue("persistClientQueue");
              enqueue("persistServerQueue");
            }),
          },
        },
        // initial: "catchingUp",
        // states: {
        //   offline: {},
        //   synced: {},
        //   catchingUp: {},
        // },
      },
    },
  });

  return createActor(machine);
}

export function useSyncedStore<S extends IModelType<any, any>>(params: {
  storeModel: S;
  id: string;
  storageAdapter: StorageAdapter;
  storeFetch: (id: string) => Promise<SnapshotIn<S>>;
  getSessionToken: () => Promise<string | undefined>;
}):
  | { isReady: false; store: null; actor: null }
  | {
      isReady: true;
      store: S["Type"];
      actor: ReturnType<typeof createSyncedStoreActor<S>>;
    } {
  const { id, storageAdapter, storeFetch, storeModel, getSessionToken } =
    params;
  const [store, setStore] = React.useState<
    | { isReady: false; store: null; actor: null }
    | {
        isReady: true;
        store: S["Type"];
        actor: ReturnType<typeof createSyncedStoreActor<S>>;
      }
  >({ isReady: false, store: null, actor: null });

  const actor = React.useMemo(() => {
    return createSyncedStoreActor({
      id,
      storageAdapter,
      storeFetch,
      storeModel,
      getSessionToken,
    });
  }, [id, storageAdapter, storeFetch, storeModel, getSessionToken]);

  React.useEffect(() => {
    let previousState = "";
    const subscription = actor.subscribe((state) => {
      console.log("synced store state", JSON.stringify(state.value));
      if (state.matches("ready") && previousState !== "ready") {
        previousState = "ready";
        const store = state.context.store as S["Type"];
        const storeActions = getMembers(store).actions;
        const storeProxy = new Proxy(store, {
          get: function (target, prop, receiver) {
            const origProp = Reflect.get(target, prop, receiver);
            if (typeof origProp === "function") {
              return function (...args: any[]) {
                // @ts-expect-error - TODO: figure out how to type this properly
                const result = origProp.apply(target, args);
                if (storeActions.includes(prop as string)) {
                  actor.send({
                    type: "CLIENT_ACTION",
                    payload: {
                      id: generateObjectId(),
                      clientTime: Date.now(),
                      type: String(prop),
                      payload: args[0],
                    },
                  });
                }
                return result;
              };
            } else {
              return origProp;
            }
          },
        });
        setStore({ isReady: true, store: storeProxy, actor });
      } else {
        previousState = state.matches("ready")
          ? "ready"
          : state.value.toString();
      }
    });
    actor.start();
    return () => {
      actor.send({ type: "STOP" });
      actor.stop();
      previousState = "";
      subscription.unsubscribe();
      setStore({ isReady: false, store: null, actor: null });
    };
  }, [actor]);

  return store;
}

const errorMessage = z.object({
  type: z.literal("ERROR"),
  error: z.string(),
});
const readyMessage = z.object({ type: z.literal("READY") });
const ackMessage = z.object({ type: z.literal("ACK"), id: z.string() });
const serverActionMessage = z.object({
  type: z.literal("SERVER_ACTION"),
  payload: z.object({
    id: z.string(),
    clientId: z.string().optional().nullable(),
    type: z.string(),
    payload: z.any(),
  }),
});

const socketMessagesSchema = z.union([
  ackMessage,
  readyMessage,
  errorMessage,
  serverActionMessage,
]);
